<template>
  <div class="consult-page">
    <div class="consult-inner">
      <router-link to="/pointe/dashboard" class="back-link">← Dashboard</router-link>

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
      </div>

      <div v-else-if="!consultation" class="not-found">
        <p>Consultation not found.</p>
      </div>

      <div v-else>
        <!-- Header -->
        <div class="consult-header">
          <div>
            <p class="consult-eyebrow">Foot 360 Consultation</p>
            <h1>{{ formatDate(consultation.consultationDate) }}</h1>
          </div>
          <div class="header-right">
            <div class="followup-box">
              <span class="followup-count">{{ consultation.followUpsRemaining }}</span>
              <span class="followup-label">free re-applications left</span>
            </div>
          </div>
        </div>

        <!-- Overall result -->
        <div class="overall-result" :class="overallClass">
          <span v-if="consultation.overallApproved === true">Overall: Approved ✓</span>
          <span v-else-if="consultation.overallApproved === false">Overall: Keep Working — you're on the right track!</span>
          <span v-else>Overall: Assessment in progress</span>
        </div>

        <!-- Resources -->
        <div v-if="hasResources" class="resources-section">
          <h2>Your Resources</h2>
          <div class="resource-links">
            <a v-if="consultation.googleDocLink" :href="consultation.googleDocLink" target="_blank" class="resource-card">
              <span class="resource-icon">📄</span>
              <span>Full Analysis</span>
            </a>
            <a v-if="consultation.loomLink" :href="consultation.loomLink" target="_blank" class="resource-card">
              <span class="resource-icon">🎥</span>
              <span>Video Review</span>
            </a>
            <a v-if="consultation.practiceGoogleDocLink" :href="consultation.practiceGoogleDocLink" target="_blank" class="resource-card">
              <span class="resource-icon">📋</span>
              <span>Practice Plan</span>
            </a>
          </div>
        </div>

        <!-- Criteria results -->
        <div v-if="criteriaWithResults.length" class="criteria-section">
          <h2>Criteria Assessment</h2>

          <div v-for="c in criteriaWithResults" :key="c.id" class="criterion-row">
            <div class="criterion-status-col">
              <span v-if="c.result === true" class="status-pass">✓</span>
              <span v-else-if="c.result === false" class="status-fail">✗</span>
              <span v-else class="status-pending">—</span>
            </div>
            <div class="criterion-body">
              <div class="criterion-title">{{ c.title }}</div>
              <div v-if="c.category" class="criterion-cat">{{ c.category }}</div>
              <div v-if="c.adminNotes" class="criterion-notes">{{ c.adminNotes }}</div>
              <div v-if="c.photoUrl" class="criterion-photo">
                <a :href="c.photoUrl" target="_blank">View Photo →</a>
              </div>
              <!-- Resource links for failing criteria -->
              <div v-if="c.result === false && c.resourceLinks && c.resourceLinks.length" class="resource-links-inline">
                <span class="resource-label">Resources:</span>
                <a v-for="link in c.resourceLinks" :key="link.url" :href="link.url" target="_blank" class="resource-link">{{ link.label }}</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Linked applications -->
        <div v-if="linkedApplications.length" class="linked-section">
          <h2>Linked Applications</h2>
          <div v-for="app in linkedApplications" :key="app.id" class="linked-app-row">
            <div>
              <strong>{{ app.cohortName }}</strong>
              <span class="status-badge" :class="app.status">{{ statusLabel(app.status) }}</span>
            </div>
            <router-link :to="'/pointe/application/' + app.id" class="btn-sm">View Results →</router-link>
          </div>
        </div>

        <!-- Follow-up CTA -->
        <div v-if="consultation.followUpsRemaining > 0" class="followup-cta">
          <h3>You have {{ consultation.followUpsRemaining }} free re-application{{ consultation.followUpsRemaining > 1 ? 's' : '' }}</h3>
          <p>
            When applications open, you can use your free re-application — no fee required.
            We'll automatically detect it when you apply.
          </p>
          <router-link to="/pointe" class="btn-outline">See Current Cohort →</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  props: {
    id: { type: String, required: true }
  },
  data () {
    return {
      consultation: null,
      criteria: [],
      linkedApplications: [],
      loading: true
    }
  },
  computed: {
    hasResources () {
      return this.consultation && (
        this.consultation.googleDocLink ||
        this.consultation.loomLink ||
        this.consultation.practiceGoogleDocLink
      )
    },
    overallClass () {
      if (!this.consultation) return ''
      if (this.consultation.overallApproved === true) return 'overall-approved'
      if (this.consultation.overallApproved === false) return 'overall-needs-work'
      return 'overall-pending'
    },
    criteriaWithResults () {
      const consultCriteria = this.consultation ? (this.consultation.criteria || {}) : {}
      return this.criteria.map(c => ({
        ...c,
        result: consultCriteria[c.id] !== undefined ? consultCriteria[c.id].passed : null,
        adminNotes: consultCriteria[c.id] ? consultCriteria[c.id].adminNotes : '',
        photoUrl: consultCriteria[c.id] ? consultCriteria[c.id].photoUrl : ''
      }))
    }
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    const [consultDoc, criteriaSnap] = await Promise.all([
      db.collection('pointe-consultations').doc(this.id).get(),
      db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get()
    ])
    if (consultDoc.exists) {
      this.consultation = { id: consultDoc.id, ...consultDoc.data() }
      const linkedIds = this.consultation.linkedApplicationIds || []
      if (linkedIds.length > 0) {
        const appPromises = linkedIds.map(aid => db.collection('pointe-applications').doc(aid).get())
        const appDocs = await Promise.all(appPromises)
        this.linkedApplications = appDocs.filter(d => d.exists).map(d => ({ id: d.id, ...d.data() }))
      }
    }
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.loading = false
  },
  methods: {
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    },
    statusLabel (status) {
      const map = {
        submitted: 'Submitted', under_review: 'Under Review',
        approved: 'Approved', needs_work: 'Needs Work',
        awaiting_video: 'Awaiting Video'
      }
      return map[status] || status
    }
  }
}
</script>

<style scoped>
.consult-page {
  min-height: 80vh;
  background: #faf9f7;
  font-family: 'Fanwood Text', Georgia, serif;
  padding: 40px 24px;
}

.consult-inner { max-width: 720px; margin: 0 auto; }

.back-link { font-size: 13px; color: #888; text-decoration: none; display: block; margin-bottom: 24px; }
.back-link:hover { color: #1a1a1a; }

.loading-wrap { padding: 60px; text-align: center; }
.not-found { padding: 40px; text-align: center; color: #888; }

.consult-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.consult-eyebrow {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: #999;
  margin: 0 0 6px;
}

.consult-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0;
}

.followup-box { text-align: center; }

.followup-count {
  display: block;
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 36px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.followup-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: .05em; display: block; }

.overall-result {
  border-radius: 4px;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
}

.overall-approved { background: #e8f5e9; color: #2e7d32; }
.overall-needs-work { background: #fce4ec; color: #c62828; }
.overall-pending { background: #fff8e1; color: #f57f17; }

/* Sections */
.resources-section, .criteria-section, .linked-section {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 20px;
}

.resources-section h2, .criteria-section h2, .linked-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 16px;
}

.resource-links { display: flex; gap: 12px; flex-wrap: wrap; }

.resource-card {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #faf9f7;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 12px 18px;
  text-decoration: none;
  color: #1a1a1a;
  font-size: 15px;
  transition: background 0.15s;
}

.resource-card:hover { background: #e8e5e1; }
.resource-icon { font-size: 20px; }

/* Criteria */
.criterion-row {
  display: flex;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #f5f4f2;
}

.criterion-row:last-child { border-bottom: none; }

.criterion-status-col { width: 32px; text-align: center; flex-shrink: 0; padding-top: 2px; }

.status-pass { color: #2e7d32; font-size: 20px; font-weight: 700; }
.status-fail { color: #c62828; font-size: 20px; font-weight: 700; }
.status-pending { color: #bbb; font-size: 18px; }

.criterion-title { font-size: 15px; font-weight: 600; color: #1a1a1a; margin-bottom: 2px; }
.criterion-cat { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .05em; }
.criterion-notes { font-size: 14px; color: #555; margin-top: 4px; line-height: 1.5; }

.criterion-photo a { font-size: 13px; color: #1a1a1a; }

.resource-links-inline { margin-top: 8px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.resource-label { font-size: 12px; color: #888; }
.resource-link {
  font-size: 12px;
  color: #1a1a1a;
  background: #f5f4f2;
  padding: 3px 10px;
  border-radius: 20px;
  text-decoration: none;
}

/* Linked apps */
.linked-app-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0eeec;
  font-size: 15px;
  gap: 12px;
}

.linked-app-row:last-child { border-bottom: none; }

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 8px;
}

.status-badge.approved { background: #e8f5e9; color: #2e7d32; }
.status-badge.needs_work { background: #fce4ec; color: #c62828; }
.status-badge.submitted { background: #e3f2fd; color: #1565c0; }

.btn-sm {
  font-size: 13px;
  color: #1a1a1a;
  text-decoration: none;
  border: 1px solid #e8e5e1;
  padding: 5px 12px;
  border-radius: 2px;
  white-space: nowrap;
}

.btn-sm:hover { border-color: #1a1a1a; }

/* Follow-up CTA */
.followup-cta {
  background: #e8eaf6;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
}

.followup-cta h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.followup-cta p { font-size: 15px; color: #555; margin: 0 0 16px; line-height: 1.6; }

.btn-outline {
  display: inline-block;
  border: 1px solid #1a1a1a;
  color: #1a1a1a;
  padding: 10px 22px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  text-decoration: none;
  border-radius: 2px;
}

.btn-outline:hover { background: #1a1a1a; color: #fff; }
</style>
